import { inject, Injectable } from '@angular/core';
import { ApplicationHelperService } from '../forms/application-helper.service';
import { ControlHelperService } from '../forms/control-helper.service';
import { ControlVisibilityService } from './control-visibility.service';
import { CommunicatorService } from 'app/shared/services/communicator.service';
import { MessageModel, VisibilityChangeModel } from 'app/shared/services/model/communicatorModel';
import {
    ContainerControl,
    ControlPath,
    ControlType,
    DataContainer,
    IValuePersistableControl,
    Qualifier,
    RepeaterControl,
    iApplyFormControl
} from 'app/shared/model';
import { CalculationService } from '../forms/application-editor/control-list/controls/calculation/calculation.service';
import { DataSharingService } from 'app/shared/services/data-sharing.service';

@Injectable()
export class ControlVisibilityRunService
{
    private readonly _applicationHelperService: ApplicationHelperService = inject(ApplicationHelperService);
    private readonly _controlListFunctionsService: ControlHelperService = inject(ControlHelperService);
    private readonly _controlVisibilityService: ControlVisibilityService = inject(ControlVisibilityService);
    private readonly _communicatorService: CommunicatorService = inject(CommunicatorService);
    private readonly _calculationService: CalculationService = inject(CalculationService);
    private readonly _dataSharingService: DataSharingService = inject(DataSharingService);

    constructor() { }

    refreshControlsVisibility(controlList: iApplyFormControl[], application: DataContainer) {
        if (!controlList?.length || !application) {
            return;
        }

        let allControls = this._applicationHelperService.flattenControlList(controlList),
            flagCalculation = false;

        // for (let i = 0; i < allControlas.length; i++) {
        //     control = allControlas[i];
        for (let control of allControls) {
            if (control.type === ControlType.Calculation) {
                // We need to update the  visibility of teh calculation control based on any rules applied,
                // but we also need to re-trigger calculations beacuse visibility changes can clear/initialise default values.
                flagCalculation = true;
            }

            let pathWithContainer = this._controlListFunctionsService.getControlPath(control, controlList);
            let subContainers = this._getSubContainersByPath(application, pathWithContainer, null);

            // for (let j = 0; j < subContainers.length; j++) {
            for (let container of subContainers) {
                let qualifierKey = container.key;
                let subContainer = container.container;

                // ALWAYS update visiblity flag - in repeaters, each row can be different...
                // if (!control.hasOwnProperty("isVisible")) ...
                control.updateIsVisible(this._controlVisibilityService.isVisible(
                    control,
                    application,
                    //subContainer.$$qualifier, not initialized yet
                    qualifierKey,
                    this._dataSharingService.controls,
                    this._dataSharingService.surveyResult));

                this.doVisibilityRun(control, qualifierKey, undefined, subContainer, undefined, this._dataSharingService.extendedControls, controlList);
            }
        }

        // recalculate after all others fields will be updated.
        if (flagCalculation) {
            this._doCalculationVisibilityRun(null, true);
        }
    };

    doVisibilityRun(
        ctl: iApplyFormControl,
        qualifier: Qualifier,
        parentCtrlVisibility: boolean,
        container: DataContainer,
        section,
        extendedControls: iApplyFormControl[],
        controls: iApplyFormControl[]): boolean {
        qualifier = qualifier || new Qualifier();

        if (ctl.lastIsVisibleValue === undefined ||
            (parentCtrlVisibility !== undefined && ctl.lastIsVisibleValue !== parentCtrlVisibility)) {
            ctl.lastIsVisibleValue = this._controlVisibilityService.isVisible(ctl, this._communicatorService.getApplicationData(), qualifier, controls, null);
            this._broadcastVisibilityUpdate(qualifier, ctl.id, ctl.lastIsVisibleValue);
            return false;
        }

        switch (ctl.type) {
            case ControlType.Group:
                this._doGroupVisibilityRun(<ContainerControl>ctl, qualifier, container, section, extendedControls, controls);
                break;

            case ControlType.Calculation:
                this._doCalculationVisibilityRun(ctl, false);
                break;

            case ControlType.Repeater:
                this._doRepeaterVisibilityRun(<RepeaterControl>ctl, qualifier, container, extendedControls, section, controls);
                break;

            case ControlType.Signaturepad:
                // Clear and initialize (both on hide and show)
                this._communicatorService.sendMessage(new MessageModel('initialiseSignaturePad', ctl.id));
                this._controlVisibilityService.initializeFieldValue(ctl, container);
                break;

            case ControlType.Search:
                this._controlVisibilityService.initializeFieldValue(ctl, container);
                break;

            case ControlType.Photobooth:
                if (ctl.lastIsVisibleValue) {
                    this._communicatorService.sendMessage(new MessageModel('resumeVideoCapture', null));
                }

            default:
                if (this._controlListFunctionsService.isValueControl(ctl.type)) {
                    this._controlVisibilityService.initializeFieldValue(ctl, container);
                }
                break;
        }

        return true;
    };

    _getSubContainersByPath(container: DataContainer, path: string | string[], pathToContainer?: Qualifier) {
        if (!container || typeof container !== 'object') {
            return [];
        }

        let pathArray: string[],
            self = this;

        if (typeof path === "string" && !!path) {
            pathArray = path.split(".");
        } else if (Array.isArray(path) && !!path && !!path.length) {
            pathArray = path;
        } else {
            return [{
                key: pathToContainer || new Qualifier(),
                container: container
            }];
        }

        let key = pathArray.shift(),
            currentContainer = container[key],
            getResult = function (childContainer, pathArray, qualifier: Qualifier, result) {
                let arrayPathClonned = JSON.parse(JSON.stringify(pathArray)),
                    res = self._getSubContainersByPath(childContainer, arrayPathClonned, qualifier);
                return result.concat(res);
            };

        if (Array.isArray(currentContainer)) {
            let result = [],
                preQualifier = pathToContainer ? pathToContainer : new Qualifier();
            for (let i = 0; i < currentContainer.length; i++) {
                let clonnedPath = new Qualifier(preQualifier.toString()),
                    childContainer = currentContainer[i];

                clonnedPath.push(new ControlPath(key, i));

                result = getResult(childContainer, pathArray, clonnedPath, result);
            }

            return result;
        } else {
            return getResult(currentContainer, pathArray, new Qualifier(key), []);
        }
    };

    _doGroupVisibilityRun(
        ctrl: ContainerControl,
        qualifier: Qualifier,
        container: DataContainer,
        section,
        extendedControls: iApplyFormControl[],
        controls: iApplyFormControl[]) {
        if (!ctrl || !ctrl.controls) {
            return;
        }

        if (ctrl.lastIsVisibleValue) {
            this._communicatorService.sendMessage(new MessageModel('resumeVideoCapture', null));
        }

        for (let i = 0; i < ctrl.controls.length; i++) {
            if (ctrl.controls[i].visibilityRule) {
                let childVisible = ctrl.lastIsVisibleValue && this._controlVisibilityService.isVisible(
                    ctrl.controls[i],
                    this._communicatorService.getApplicationData(),
                    qualifier,
                    extendedControls,
                    null);
                this._broadcastVisibilityUpdate(qualifier, ctrl.controls[i].id, !!childVisible);
            } else {
                this.doVisibilityRun(ctrl.controls[i], qualifier, ctrl.lastIsVisibleValue, container, section, extendedControls, controls);
            }
        }
    };

    _doCalculationVisibilityRun(ctl: iApplyFormControl, avoidControlChecking?: boolean) {
        // If the control is visible, OR if it's always hidden but wants to be visible, execute the calculations.
        let controlChecking = ctl && (ctl.lastIsVisibleValue || (ctl.wantsVisible && ctl.isHidden));
        if (avoidControlChecking || controlChecking) {
            return this._calculationService.executeAndSetValues(
                this._dataSharingService.formId,
                this._dataSharingService?.product?.version ?? null,
                this._dataSharingService?.product?.linkApplicationsToVersion ?? false,
                this._dataSharingService.applicationId,
                this._communicatorService.applicationData,
                this._dataSharingService?.currentPage?.pageId ?? -1,
                this._dataSharingService.controls
            );
        }
        return;
        //else {
        // Calculation control values are re-calculated when watched fields change, and as such should not be affected by being hidden.
        // initializeFieldValue(ctl); // <- Don't do this!
        //}
    };

    _doRepeaterVisibilityRun(
        ctl: RepeaterControl,
        qualifier: Qualifier,
        container: DataContainer,
        extendedControls: iApplyFormControl[],
        section,
        controls: iApplyFormControl[]) {
        if (!ctl) {
            return;
        }

        let hasPersistValueControls = !!this._controlListFunctionsService.findOne(ctl.controls || [], function (c: iApplyFormControl) {
            return (<IValuePersistableControl>c).persistValue;
        });

        let prefix = qualifier ? qualifier : new Qualifier();
        let containerCtrl = (container && container[ctl.name]) || [];
        for (let i = 0; i < containerCtrl.length; i++) {
            let clonPref: Qualifier = new Qualifier(prefix.toString());
            clonPref.push(new ControlPath(ctl.name, i));
            this._doGroupVisibilityRun(ctl, clonPref, containerCtrl[i], section, extendedControls, controls);
        }

        if (!hasPersistValueControls) {
            this._controlVisibilityService.initializeFieldValue(ctl, container);
        }

        if (ctl.lastIsVisibleValue && !!ctl.masterControl) {
            this._communicatorService.sendMessage(new MessageModel('RestoreMissedRows' + ctl.name, null));
        }
    };

    _broadcastVisibilityUpdate(qualifier: Qualifier, controlId: string | number, visible: boolean) {
        this._communicatorService.sendVisibilityChangeMessage(new VisibilityChangeModel(controlId, visible, qualifier));
    };
}
